import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useToast } from '../../contexts/ToastContext';
import { formatPrice } from '../../utils/priceFormatter';
import { API_BASE } from '../../utils/url';
import BottomNav from '../../components/layouts/BottomNav';

const CartPage = () => {
  const navigate = useNavigate();
  const { items, cartTotal, cartCount, removeFromCart, updateQuantity, clearCart } = useCart();
  const { showSuccess, showError } = useToast();

  // Check authentication when trying to create quotation
  const handleCreateQuotation = () => {
    if (items.length === 0) {
      showError('Cart is empty');
      return;
    }

    // Login check removed to allow guest access
    navigate('/quotation');
  };

  const handleQuantityChange = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      showSuccess('Item removed from cart');
    } else {
      updateQuantity(productId, newQuantity);
    }
  };

  const handleRemoveItem = (productId) => {
    removeFromCart(productId);
    showSuccess('Item removed from cart');
  };

  const handleClearCart = () => {
    clearCart();
    showSuccess('Cart cleared');
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return API_BASE + imagePath.replace(/^\/+/, '');
  };

  if (items.length === 0) {
    return (
      <div style={{ background: "#fff", minHeight: "100vh", paddingBottom: 74 }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", padding: "18px 18px 10px 18px",
          borderBottom: "1px solid #f3f3f3", background: "#fff", position: "sticky", top: 0, zIndex: 10
        }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: "none",
              border: "none",
              fontSize: 26,
              color: "#222",
              cursor: "pointer",
              lineHeight: 1,
              marginRight: 14
            }}
            aria-label="Back"
          >
            &larr;
          </button>
          <span style={{
            fontWeight: 700,
            fontSize: 22,
            color: "#232323"
          }}>
            Shopping Cart
          </span>
        </div>

        {/* Empty Cart */}
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "60px 20px",
          textAlign: "center"
        }}>
          <div style={{
            fontSize: 64,
            marginBottom: 20,
            color: "#ddd"
          }}>
            🛒
          </div>
          <h2 style={{
            fontSize: 24,
            fontWeight: 600,
            color: "#333",
            marginBottom: 12
          }}>
            Your cart is empty
          </h2>
          <p style={{
            fontSize: 16,
            color: "#666",
            marginBottom: 30
          }}>
            Add some products to get started
          </p>
          <button
            onClick={() => navigate('/products')}
            style={{
              background: "#333333",
              color: "#ffffff",
              border: "none",
              borderRadius: 8,
              padding: "14px 24px",
              fontSize: 16,
              fontWeight: 600,
              cursor: "pointer",
              transition: "background-color 0.2s ease"
            }}
            onMouseEnter={(e) => e.target.style.background = "#555555"}
            onMouseLeave={(e) => e.target.style.background = "#333333"}
          >
            Browse Products
          </button>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div style={{ background: "#fff", minHeight: "100vh", paddingBottom: 74 }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "18px 18px 10px 18px",
        borderBottom: "1px solid #f3f3f3", background: "#fff", position: "sticky", top: 0, zIndex: 10
      }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: "none",
              border: "none",
              fontSize: 26,
              color: "#222",
              cursor: "pointer",
              lineHeight: 1,
              marginRight: 14
            }}
            aria-label="Back"
          >
            &larr;
          </button>
          <span style={{
            fontWeight: 700,
            fontSize: 22,
            color: "#232323"
          }}>
            Shopping Cart ({cartCount})
          </span>
        </div>

        <button
          onClick={handleClearCart}
          style={{
            background: "none",
            border: "none",
            color: "#ef4444",
            fontSize: 14,
            cursor: "pointer",
            fontWeight: 500
          }}
        >
          Clear All
        </button>
      </div>

      {/* Cart Items */}
      <div style={{ padding: "16px" }}>
        {items.map((item) => (
          <div
            key={item.id}
            style={{
              background: "#fcfcfd",
              borderRadius: 12,
              padding: 16,
              marginBottom: 12,
              boxShadow: "0 1px 7px 0 rgba(40,44,63,0.04)",
              border: "1px solid #f0f0f0"
            }}
          >
            <div style={{ display: "flex", gap: 12 }}>
              {/* Product Image */}
              <div style={{
                width: 80,
                height: 80,
                borderRadius: 8,
                overflow: "hidden",
                background: "#eee",
                flexShrink: 0
              }}>
                {item.image ? (
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover"
                    }}
                  />
                ) : (
                  <div style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#bbb",
                    fontSize: 12
                  }}>
                    No Img
                  </div>
                )}
              </div>

              {/* Product Details */}
              <div style={{ flex: 1 }}>
                <h3 style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#232323",
                  marginBottom: 4
                }}>
                  {item.name}
                </h3>

                {/* Price hidden as per user request */}

                {/* Quantity Controls */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8
                }}>
                  <button
                    onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                    style={{
                      width: 32,
                      height: 32,
                      border: "1px solid #ddd",
                      background: "#fff",
                      borderRadius: 4,
                      cursor: "pointer",
                      fontSize: 16,
                      fontWeight: "bold"
                    }}
                  >
                    -
                  </button>

                  <span style={{
                    fontSize: 16,
                    fontWeight: 500,
                    minWidth: 30,
                    textAlign: "center"
                  }}>
                    {item.quantity}
                  </span>

                  <button
                    onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                    style={{
                      width: 32,
                      height: 32,
                      border: "1px solid #ddd",
                      background: "#fff",
                      borderRadius: 4,
                      cursor: "pointer",
                      fontSize: 16,
                      fontWeight: "bold"
                    }}
                  >
                    +
                  </button>

                  <button
                    onClick={() => handleRemoveItem(item.id)}
                    style={{
                      marginLeft: "auto",
                      background: "none",
                      border: "none",
                      color: "#ef4444",
                      fontSize: 14,
                      cursor: "pointer",
                      fontWeight: 500
                    }}
                  >
                    Remove
                  </button>
                </div>

                {/* Item total hidden as per user request */}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Cart Summary */}
      <div style={{
        position: "fixed",
        bottom: 74,
        left: 0,
        right: 0,
        background: "#fff",
        borderTop: "1px solid #f0f0f0",
        padding: "16px",
        boxShadow: "0 -2px 10px rgba(0,0,0,0.1)"
      }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16
        }}>
          <span style={{
            fontSize: 18,
            fontWeight: 600,
            color: "#333"
          }}>
            Total ({cartCount} items):
          </span>
          {/* Total hidden as per user request */}
        </div>

        <button
          onClick={handleCreateQuotation}
          style={{
            width: "100%",
            padding: "14px 20px",
            background: "#000000",
            color: "#ffffff",
            border: "none",
            borderRadius: 8,
            fontSize: 16,
            fontWeight: 600,
            cursor: "pointer",
            transition: "background-color 0.2s ease"
          }}
          onMouseEnter={(e) => e.target.style.background = "#333333"}
          onMouseLeave={(e) => e.target.style.background = "#000000"}
        >
          Create Quotation
        </button>
      </div>

      <BottomNav />
    </div>
  );
};

export default CartPage;
